import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import Dashboard from './components/Dashboard';
import SendEmail from './components/SendEmail';
import EmailHistory from './components/EmailHistory';
import Jobs from './components/Jobs';
import WebhookLogs from './components/WebhookLogs';
import Login from './components/Login';
import Profile from './components/Profile';
import ErrorModal from './components/ErrorModal';
import SessionTimer from './components/SessionTimer';
import BoardingPassEmailer from './components/BoardingPassEmailer';
import { setGlobalErrorHandler } from './services/api';

// Get session duration from environment variable (in hours), default to 1 hour
// Convert to milliseconds
const SESSION_DURATION_HOURS = parseFloat(process.env.REACT_APP_SESSION_DURATION_HOURS || '1');
const SESSION_DURATION = SESSION_DURATION_HOURS * 60 * 60 * 1000; // Convert hours to milliseconds

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [errorModal, setErrorModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    details: null,
    type: 'error',
  });

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('sessionStartTime');
  };

  const handleLogin = (userData) => {
    setIsAuthenticated(true);
    setUser(userData);
    localStorage.setItem('authToken', 'authenticated');
    localStorage.setItem('user', JSON.stringify(userData));
    // Set session start time
    localStorage.setItem('sessionStartTime', Date.now().toString());
  };

  const handleSessionExpire = () => {
    handleLogout();
    setErrorModal({
      isOpen: true,
      title: 'Session Expired',
      message: 'Your session has expired due to inactivity. Please log in again.',
      details: null,
      type: 'warning',
    });
  };

  useEffect(() => {
    // Check if user is logged in from localStorage
    const authToken = localStorage.getItem('authToken');
    const userData = localStorage.getItem('user');
    const sessionStartTime = localStorage.getItem('sessionStartTime');
    
    if (authToken && userData) {
      // Check if session has expired
      if (sessionStartTime) {
        const elapsed = Date.now() - parseInt(sessionStartTime, 10);
        if (elapsed >= SESSION_DURATION) {
          // Session expired, clear and logout
          handleLogout();
          setErrorModal({
            isOpen: true,
            title: 'Session Expired',
            message: 'Your session has expired. Please log in again.',
            details: null,
            type: 'warning',
          });
          return;
        }
      }
      
      setIsAuthenticated(true);
      setUser(JSON.parse(userData));
    }

    // Set up global error handler
    setGlobalErrorHandler((errorData) => {
      setErrorModal({
        isOpen: true,
        title: errorData.title || 'Error',
        message: errorData.message || 'An error occurred',
        details: errorData.details || null,
        type: errorData.type || 'error',
      });
    });

    // Cleanup
    return () => {
      setGlobalErrorHandler(null);
    };
  }, []);

  const closeErrorModal = () => {
    setErrorModal({
      isOpen: false,
      title: '',
      message: '',
      details: null,
      type: 'error',
    });
  };

  return (
    <Router>
      <div className="App">
        {/* Global Error Modal */}
        <ErrorModal
          isOpen={errorModal.isOpen}
          onClose={closeErrorModal}
          title={errorModal.title}
          message={errorModal.message}
          details={errorModal.details}
          type={errorModal.type}
        />

        {isAuthenticated ? (
          <>
            <nav className="navbar">
              <div className="nav-container">
                <div className="nav-logo-section">
                  <div className="nav-logo-icon">🚀</div>
                  <h1 className="nav-logo">RideSharePro</h1>
                  <span className="nav-logo-subtitle">Email Marketing Automation</span>
                </div>
                <div className="nav-links">
                  <Link to="/" className="nav-link">Dashboard</Link>
                  <Link to="/send" className="nav-link">Send Email</Link>
                  <Link to="/boarding-pass" className="nav-link">Boarding Pass</Link>
                  <Link to="/jobs" className="nav-link">Jobs</Link>
                  <Link to="/history" className="nav-link">History</Link>
                  <Link to="/webhook-logs" className="nav-link">Webhook Logs</Link>
                  <Link to="/profile" className="nav-link">Profile</Link>
                  <div className="nav-user">
                    {user && <span className="nav-username">{user.username || user.email}</span>}
                    {/* Session Timer in header */}
                    <SessionTimer onSessionExpire={handleSessionExpire} />
                    <button className="nav-link nav-logout" onClick={handleLogout}>
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            </nav>

            <main className="main-content">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/send" element={<SendEmail user={user} />} />
                <Route path="/boarding-pass" element={<BoardingPassEmailer />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/history" element={<EmailHistory />} />
                <Route path="/webhook-logs" element={<WebhookLogs />} />
                <Route path="/profile" element={<Profile user={user} />} />
              </Routes>
            </main>
          </>
        ) : (
          <Login onLogin={handleLogin} />
        )}
      </div>
    </Router>
  );
}

export default App;

